import { createContext, ReactNode, useState, useContext } from "react";
import { IUserContext, AppUser, LoginHandler } from "./types";
import { useApplicationUserApiClient } from "../../hooks/useApplicationUserApiClient";

const defaultContextValues: IUserContext = {
  user: null,
  accessToken: null,
  clientType: null,
  login: (_token: string, _clientType: "consumer" | "banker") => {},
  checkPermission: (_actionName: string, _resourceName?: string) =>
    new Promise((res) => res(false)),
  checkPermissions: (_requiredPermissions) => new Promise((res) => res([])),
  logout: () => {},
};

export const UserContext = createContext<IUserContext>(defaultContextValues);

export default function UserContextProvider({ children }: { children: Readonly<ReactNode> }) {
  const [isBusyDoingWork, setIsBusyDoingWork] = useState<boolean>(false);
  const [user, setUser] = useState<AppUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [clientType, setClientType] = useState<"consumer" | "banker" | null>(null);

  const applicationUserApiClient = useApplicationUserApiClient();

  const login: LoginHandler = (token: string, clientType: "consumer" | "banker") => {
    setAccessToken(token);
    setClientType(clientType);

    if (user != null) {
      return;
    }

    setIsBusyDoingWork(true);

    if (!isBusyDoingWork) {
      // Use the appropriate login method based on client type
      const loginPromise =
        clientType === "consumer"
          ? applicationUserApiClient.loginConsumer(token)
          : applicationUserApiClient.loginBanker(token);

      loginPromise
        .then((user) => {
          setUser(user);
          setIsBusyDoingWork(false);
        })
        .catch((error) => {
          console.error("Failed to sign in user:", error);
          setIsBusyDoingWork(false);
        });
    }
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    setClientType(null);
  };

  const checkPermission = (actionName: string, resourceName?: string) => {
    if (!user?.id || !accessToken) {
      return new Promise<boolean>((resolve) => resolve(false));
    }

    return applicationUserApiClient.checkPermission(user.id, actionName, resourceName, accessToken);
  };

  const checkPermissions = (requiredPermissions: any[]) => {
    if (!user?.id || !accessToken) {
      return new Promise<any[]>((resolve) => resolve([]));
    }

    return applicationUserApiClient.checkPermissions(user.id, requiredPermissions, accessToken);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        accessToken,
        clientType,
        login,
        logout,
        checkPermission,
        checkPermissions,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserContextProvider");
  }
  return context;
};
